/**
 * <testimonial-carousel> — the review quote slider.
 *
 * Shows as many cards as fit and only behaves as a carousel when they overflow: with
 * four or fewer cards on a wide screen Swiper's `watchOverflow` disables dragging and
 * hides the controls, so a short review list renders as a plain row.
 *
 * Pagination bullets and arrows are the theme's own markup, styled from tokens to match
 * Figma's Bullet Navigation component, rather than Swiper's defaults.
 *
 * With `data-randomize` the slides are shuffled once per page load, so the handful a
 * visitor sees first is not always the same handful. The order is only ever changed
 * before Swiper reads the DOM — reordering afterwards would leave its pagination
 * pointing at the wrong slides.
 */

// Wrapped so nothing here reaches global scope: these are classic scripts, and a
// top-level name shared with another file makes the whole file throw before it can
// register its element. Also what makes a duplicate <script> tag for this file safe.
(() => {
  class TestimonialCarousel extends HTMLElement {
    connectedCallback() {
      if (typeof window.Swiper === 'undefined') {
        window.addEventListener('load', () => this.init(), { once: true });
        return;
      }

      this.init();
    }

    disconnectedCallback() {
      this.swiper?.destroy(true, true);
    }

    /**
     * Fisher-Yates over the slide elements. Skipped in the theme editor, where a merchant
     * reordering blocks needs to see that order reflected rather than reshuffled.
     */
    shuffleSlides(wrapper) {
      if (!wrapper || !this.hasAttribute('data-randomize')) return;
      if (window.Shopify?.designMode) return;

      const slides = [...wrapper.children];
      for (let i = slides.length - 1; i > 0; i -= 1) {
        const j = Math.floor(Math.random() * (i + 1));
        [slides[i], slides[j]] = [slides[j], slides[i]];
      }

      wrapper.append(...slides);
    }

    /**
     * Drops the tail of the list. One bullet per page means a long review list runs past
     * both arrows and off a phone screen — twenty reviews rendered twenty bullets. With
     * the order shuffled above, trimming shows a different subset each visit rather than
     * always hiding the same reviews, so nothing is effectively unpublished.
     */
    limitSlides(wrapper) {
      const max = Number(this.dataset.maxVisible);
      if (!wrapper || !max) return;

      [...wrapper.children].slice(max).forEach((slide) => slide.remove());
    }

    init() {
      if (typeof window.Swiper === 'undefined') return;

      const container = this.querySelector('[data-carousel]');
      if (!container) return;

      const wrapper = container.querySelector('.swiper-wrapper');
      this.shuffleSlides(wrapper);
      this.limitSlides(wrapper);

      this.swiper = new window.Swiper(container, {
        // Card widths come from CSS below 1024 so the next one peeks past the edge.
        slidesPerView: 'auto',
        spaceBetween: 16,
        watchOverflow: true,
        slideToClickedSlide: true,
        breakpoints: {
          // Figma fits four 312.5px cards across the 1280 container with 10px between.
          1024: { slidesPerView: 4, spaceBetween: 10 },
        },
        navigation: {
          prevEl: this.querySelector('[data-carousel-prev]'),
          nextEl: this.querySelector('[data-carousel-next]'),
        },
        pagination: {
          el: this.querySelector('[data-carousel-pagination]'),
          clickable: true,
          bulletClass: 'carousel-nav__bullet',
          bulletActiveClass: 'carousel-nav__bullet--active',
        },
        keyboard: { enabled: true, onlyInViewport: true },
      });

      // Hide the whole control row when everything already fits.
      this.classList.toggle('is-static', this.swiper.isLocked);
      this.swiper.on('lock', () => this.classList.add('is-static'));
      this.swiper.on('unlock', () => this.classList.remove('is-static'));
    }
  }

  if (!customElements.get('testimonial-carousel')) {
    customElements.define('testimonial-carousel', TestimonialCarousel);
  }
})();
